<template>
  <div>
    <main class="z-[1] relative bg-white space-y-9 md:space-y-12">
      <!-- Hero / Intro (match collection containers) -->
      <section class="relative z-[1] border-t border-black">
        <div class="relative w-full px-4 py-8 sm:px-6 lg:px-8 pt-32 pb-16 mb-12 bg-light-pink-200">
          <div class="mx-auto max-w-screen-2xl text-black">
            <div class="flex w-full flex-1 flex-col space-y-6 text-dark-teal-600">
              <div class="flex w-full max-w-lg flex-col">
                <div class="w-full flex-1 space-y-2 mt-16">
                  <div class="text-xl md:text-2xl">{{ t('pages.humans.hero.kicker') }}</div>
                  <h1 class="text-5xl font-medium md:text-6xl md:leading-[0.9] lg:text-8xl antialiased pt-8">{{ t('pages.humans.hero.title') }}</h1>
                  <h2 class="md:text-md text-dark-teal-600" role="doc-subtitle">{{ t('pages.humans.hero.subtitle') }}</h2>
                </div>
              </div>
              <div class="flex w-full flex-1 gap-3">
                <!-- Search input inspired by homepage CTA layers -->
                <div class="flex-1 [&>div]:!block">
                  <div class="relative inline-block md:w-1/2 w-full group">
                    <div class="relative z-30 w-full h-14 flex items-center rounded border border-dark-teal-600 bg-white px-4">
                      <span aria-hidden="true" class="block w-5 h-5 text-black mr-2">
                        <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="m15.89 16.647-4.964-4.963a5 5 0 0 1-1.37.735q-.746.26-1.54.26-1.949 0-3.314-1.364T3.337 8 4.7 4.687 8.015 3.32q1.948 0 3.314 1.365T12.695 8a4.6 4.6 0 0 1-.268 1.563 5 5 0 0 1-.727 1.346l4.963 4.963zm-7.874-5.05q1.506-.001 2.551-1.046T11.612 8t-1.045-2.551Q9.523 4.404 8.016 4.404T5.465 5.449 4.42 8t1.045 2.551q1.044 1.045 2.551 1.045" fill="currentColor"></path></svg>
                      </span>
                      <input
                        v-model="query"
                        autocapitalize="off"
                        autocomplete="off"
                        autocorrect="off"
                        class="flex-1 outline-none bg-transparent placeholder-black/50 text-black"
                        id="searchInput"
                        name="searchInput"
                        type="search"
                        :placeholder="t('pages.humans.search.placeholder')"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Controls and Count -->
      <section class="px-4 sm:px-6 lg:px-8">
        <div class="mx-auto max-w-screen-2xl">
          <div class="flex items-center justify-between gap-3">
            <div class="shrink-0"><span class="text-gray-600">{{ artistCountLabel }}</span></div>
            <div class="shrink-0">
              <div class="px-3 py-1 text-sm bg-white">{{ t('pages.humans.controls.sortLabel') }}</div>
            </div>
          </div>
    <!-- Data source badge -->
    <div class="mt-6">
      <ClientOnly>
        <span class="inline-flex items-center gap-2 px-3 py-1 text-xs border rounded"
          :class="dataSource === 'airtable' ? 'border-green-500 text-green-700 bg-green-50' : 'border-yellow-500 text-yellow-700 bg-yellow-50'">
          <svg v-if="dataSource === 'airtable'" class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path d="M16.707 5.293a1 1 0 0 1 0 1.414l-7.25 7.25a1 1 0 0 1-1.414 0l-3.25-3.25a1 1 0 1 1 1.414-1.414l2.543 2.543 6.543-6.543a1 1 0 0 1 1.414 0z"/></svg>
          <svg v-else class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path d="M8.257 3.099c.765-1.36 2.72-1.36 3.485 0l6.518 11.59c.75 1.333-.213 2.986-1.742 2.986H3.48c-1.53 0-2.492-1.653-1.743-2.986l6.52-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-2a1 1 0 01-1-1V7a1 1 0 112 0v4a1 1 0 01-1 1z"/></svg>
          <span class="ml-auto">
            {{ dataSource === 'airtable' ? 'Data OK' : 'Data source: DEMO (Fake Data Loaded)' }}
            <template v-if="dataSource === 'fallback' && dataReason">
              — Reason: 
              <span class="font-mono">
                {{ dataReason }}
              </span>
            </template>
          </span>
        </span>
      </ClientOnly>
    </div>
        </div>
      </section>

      <!-- Grid -->
      <section class="px-4 sm:px-6 lg:px-8">
        <div class="mx-auto max-w-screen-2xl">
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <article v-for="a in paginatedArtists" :key="a.slug" class="col-span-1">
              <div class="relative">
                <div
                  v-if="getMissingFields(a).length"
                  class="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-sm z-10"
                  :title="`Missing: ${getMissingFields(a).join(', ')}`"
                />
                <NuxtLink class="group flex flex-col gap-3" :to="`/of/${a.slug}`">
                  <div class="relative block w-full aspect-square overflow-hidden bg-gray-100">
                    <template v-if="resolveForDisplay(a.photo, a.slug).provider">
                    <NuxtImg
                      v-bind="resolveForDisplay(a.photo, a.slug)"
                        :provider="resolveForDisplay(a.photo, a.slug).provider"
                        :alt="a.name"
                        :title="a.name"
                        class="w-full h-full object-cover transition duration-300 ease-out group-hover:scale-110"
                        sizes="sm:320px md:360px lg:420px"
                        :modifiers="{ quality: 'auto', format: 'auto' }"
                      />
                    </template>
                    <img
                      v-else
                      :src="resolveForDisplay(a.photo, a.slug).src"
                      :alt="a.name"
                      :title="a.name"
                      class="w-full h-full object-cover transition duration-300 ease-out group-hover:scale-110"
                    />
                  </div>
                  <div class="flex flex-col">
                    <h3 class="font-bold transition-colors duration-300 group-hover:text-gray-500">{{ a.name }}</h3>
                    <p class="transition-colors duration-300 group-hover:text-gray-500">
                      {{ t('pages.humans.card.collaborations', { count: getNumCollaborations(a) }) }}
                    </p>
                  </div>
                </NuxtLink>
              </div>
            </article>
          </div>
          <!-- Pagination -->
          <div class="flex w-full items-center justify-center gap-4 py-24">
            <button 
              class="px-3 py-2 disabled:opacity-50 hover:border-b-black disabled:hover:border-transparent border-b border-transparent"
              :disabled="page <= 1"
              @click="goToPage(page - 1)">
              {{ t('pages.humans.pagination.prev') }}
            </button>
            <div class="flex items-center gap-2">
              <button
                v-for="n in totalPages"
                :key="n"
                class="w-9 h-9 border-b flex items-center justify-center"
                :class="n === page ? 'border-black font-bold text-black' : 'bg-white text-black border-transparent hover:border-black'"
                @click="goToPage(n)">
                {{ n }}
              </button>
            </div>
            <button 
              class="px-3 py-2 disabled:opacity-50 hover:border-b-black disabled:hover:border-transparent border-b border-transparent"
              :disabled="page >= totalPages"
              @click="goToPage(page + 1)">
              {{ t('pages.humans.pagination.next') }}
            </button>
          </div>
        </div>
      </section>
    </main>
  </div>
  
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useArtists } from '~/composables/useArtists'
import { useRoute, useRouter, useI18n } from '#imports'
import { useImageCdn } from '~/composables/useImageCdn'
import { useCdnDiagnostics } from '~/composables/useCdnDiagnostics'
import { useSeo } from '~/composables/useSeo'

const { getAllArtists, getArtistProducts, dataSource, dataReason } = useArtists()
const { resolveForDisplay } = useImageCdn()

const query = ref('')
// Sorting is fixed to alphabetical (A–Z)
const pageSize = 12
const route = useRoute()
const router = useRouter()
const page = ref<number>(parseInt((route.query.page as string) || '1', 10) || 1)

const artists = computed(() => getAllArtists())

const filteredArtists = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return artists.value
  return artists.value.filter(a => a.name.toLowerCase().includes(q))
})

const getNumCollaborations = (artist: any) => {
  return getArtistProducts(artist.name, artist).length
}

const sortedArtists = computed(() => {
  const list = [...filteredArtists.value]
  return list.sort((a, b) => a.name.localeCompare(b.name))
})

const totalPages = computed(() => Math.max(1, Math.ceil(sortedArtists.value.length / pageSize)))

const paginatedArtists = computed(() => {
  const start = (page.value - 1) * pageSize
  return sortedArtists.value.slice(start, start + pageSize)
})

watch([sortedArtists, query], () => {
  // Reset to first page on filter/sort change
  goToPage(1)
})

const goToPage = (n: number) => {
  const clamped = Math.min(Math.max(1, n), totalPages.value)
  page.value = clamped
  router.replace({ query: { ...route.query, page: String(clamped) } })
}

const { t } = useI18n()

useSeo({
  title: t('pages.humans.meta.title'),
  description: t('pages.humans.meta.description'),
  keywords: 'ommi.shop artists, makers, artisans, creators, collaborators, artist directory',
  type: 'website'
})

const artistCountLabel = computed(() => t('pages.humans.controls.count', { count: filteredArtists.value.length }))

const getMissingFields = (a: any): string[] => {
  const missing: string[] = []
  if (!a?.photo) missing.push('photo')
  if (!a?.tagline) missing.push('tagline')
  if (!a?.bio) missing.push('bio')
  if (!a?.backgroundColor) missing.push('backgroundColor')
  if (!Array.isArray(a?.gallery) || a.gallery.length === 0) missing.push('gallery')
  if (!Array.isArray(a?.links) || a.links.length === 0) missing.push('links')
  if (!a?.craft) missing.push('craft')
  return missing
}
</script>


